// ============================================================
// Store messages — carousel mobile con snap e loop infinito
// ============================================================
// Su mobile (< 768px) il contenitore dei messaggi scorre in orizzontale
// con scroll-snap (vedi _mm-store-messages.scss). Per permettere il loop
// (dall'ultimo al primo andando avanti, e viceversa) si clonano l'ultimo
// messaggio in testa e il primo in coda: quando lo scroll si ferma su un
// clone, si salta istantaneamente al messaggio reale corrispondente.

const MOBILE_MQ = '(max-width: 767.98px)';
const SCROLL_END_FALLBACK_MS = 120;

class StoreMessagesCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('.container');
    this.messages = this.track ? [...this.track.querySelectorAll(':scope > .message')] : [];
    this.clones = [];
    this.active = false;
    this.scrollTimer = null;

    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  get count() {
    return this.messages.length;
  }

  slideWidth() {
    return this.track.clientWidth;
  }

  makeClone(message) {
    const clone = message.cloneNode(true);
    clone.classList.add('message--clone');
    clone.setAttribute('aria-hidden', 'true');
    // Niente animazione on-scroll sui cloni (né sul nodo né sui figli).
    [clone, ...clone.querySelectorAll('[data-cc-animate]')].forEach(el => {
      el.removeAttribute('data-cc-animate');
      el.removeAttribute('data-cc-animate-delay');
      el.classList.remove('cc-animate-in', 'cc-animate-init');
    });
    // Un solo link raggiungibile da tastiera per messaggio: quello reale.
    clone.querySelectorAll('a').forEach(a => a.setAttribute('tabindex', '-1'));
    return clone;
  }

  enable() {
    if (this.active || this.count < 2) return;
    this.active = true;

    const first = this.messages[0];
    const last = this.messages[this.count - 1];
    const cloneLast = this.makeClone(last);
    const cloneFirst = this.makeClone(first);
    this.track.prepend(cloneLast);
    this.track.append(cloneFirst);
    this.clones = [cloneLast, cloneFirst];

    this.jumpTo(1);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('scrollend', this.onScrollEnd);
    window.addEventListener('resize', this.onResize);
  }

  disable() {
    if (!this.active) return;
    this.active = false;

    this.track.removeEventListener('scroll', this.onScroll);
    this.track.removeEventListener('scrollend', this.onScrollEnd);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.scrollTimer);

    this.clones.forEach(c => c.remove());
    this.clones = [];
    this.track.scrollLeft = 0;
  }

  // Indice della slide corrente contando anche i cloni:
  // 0 = clone ultimo, 1..count = messaggi reali, count + 1 = clone primo.
  currentIndex() {
    return Math.round(this.track.scrollLeft / this.slideWidth());
  }

  jumpTo(index) {
    const left = index * this.slideWidth();
    if ('scrollTo' in this.track) {
      this.track.scrollTo({ left, behavior: 'instant' });
    } else {
      this.track.scrollLeft = left;
    }
  }

  onScroll() {
    // Fallback per browser senza evento scrollend.
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(this.onScrollEnd, SCROLL_END_FALLBACK_MS);
  }

  onScrollEnd() {
    clearTimeout(this.scrollTimer);
    const index = this.currentIndex();
    if (index === 0) {
      this.jumpTo(this.count);
    } else if (index === this.count + 1) {
      this.jumpTo(1);
    }
  }

  onResize() {
    // La larghezza slide cambia: riallinea alla slide corrente.
    const index = Math.min(Math.max(this.currentIndex(), 1), this.count);
    this.jumpTo(index);
  }
}

const carousels = new WeakMap();
const mq = window.matchMedia(MOBILE_MQ);

function setup(root) {
  if (root.classList.contains('desktop-only')) return;
  let carousel = carousels.get(root);
  if (!carousel) {
    carousel = new StoreMessagesCarousel(root);
    carousels.set(root, carousel);
  }
  if (mq.matches) carousel.enable();
  else carousel.disable();
}

function setupAll() {
  document.querySelectorAll('[data-mm-store-messages]').forEach(setup);
}

mq.addEventListener('change', setupAll);

// Sezioni ricaricate nell'editor del tema.
document.addEventListener('shopify:section:load', evt => {
  evt.target.querySelectorAll('[data-mm-store-messages]').forEach(setup);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupAll);
} else {
  setupAll();
}
